'use client';

import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { getSafeRegionCodeFromClientCookies } from '../../../utils/geo-data';

type GeoContext = {
  isReady: boolean;
  regionCode: StateAbbreviation | undefined;
  setRegionCode: (code: StateAbbreviation) => void;
  initialRegionCode: StateAbbreviation | undefined;
};

const GeoContext = createContext<GeoContext | undefined>(undefined);

type GeoProviderProps = {
  initialLocation?: StateAbbreviation;
};

export function GeoProvider({ children, initialLocation }: PropsWithChildren<GeoProviderProps>) {
  const [isReady, setIsReady] = useState(false);
  const [regionCode, setRegionCode] = useState<StateAbbreviation | undefined>(initialLocation);
  const [initialRegionCode, setInitialRegionCode] = useState<StateAbbreviation | undefined>(
    initialLocation,
  );

  useEffect(() => {
    const clientRegionCode = getSafeRegionCodeFromClientCookies();
    if (clientRegionCode) {
      setRegionCode(clientRegionCode);
      setInitialRegionCode(clientRegionCode);
    }
    setIsReady(true);
  }, []);

  return (
    <GeoContext.Provider value={{ isReady, regionCode, setRegionCode, initialRegionCode }}>
      {children}
    </GeoContext.Provider>
  );
}

export const useGeo = (): GeoContext => {
  const context = useContext(GeoContext);

  if (!context) {
    throw new Error('useGeo must be used within a GeoProvider');
  }

  return context;
};
